/**
 * Force Protection Dialog
 * Per-geofence trusted-device baselining and watchlist management for DFP.
 *
 *   - Baseline tab: run a time-boxed scan of unique devices seen in the fence,
 *     review the list, and bulk-trust selected devices (scoped to the fence).
 *     A periodic-review banner surfaces "new since last review" devices.
 *   - Trusted tab: review/remove existing exemptions.
 *   - Watchlist tab: add/remove threat entries (exact or pattern), global or
 *     scoped to this fence.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Typography, Table, TableHead, TableRow, TableCell, TableBody, Checkbox,
  Chip, TextField, MenuItem, Alert, CircularProgress, IconButton, Tooltip,
  Select, FormControl, InputLabel, FormControlLabel,
} from '@mui/material';
import {
  Delete as DeleteIcon, Shield as ShieldIcon, Refresh as RefreshIcon,
  PlayArrow as PlayIcon, Stop as StopIcon, VerifiedUser as TrustIcon,
  Warning as WarningIcon, FactCheck as FactCheckIcon,
} from '@mui/icons-material';
import PromoteToPacketDialog, { PromoteTarget } from '@signals/app-shell/components/intel/PromoteToPacketDialog';
import { useFeatureFlagsStore } from '@signals/app-shell/stores/featureFlagsStore';
import {
  forceProtectionService, BaselineDevice, BaselineSession, TrustedDevice,
  WatchlistEntry,
} from '../services/forceProtectionService';

interface ForceProtectionDialogProps {
  open: boolean;
  onClose: () => void;
  geofenceId: number | null;
  geofenceName?: string;
}

const SIGNAL_TYPES = ['wifi', 'bluetooth', 'cellular'];

const ForceProtectionDialog: React.FC<ForceProtectionDialogProps> = ({
  open, onClose, geofenceId, geofenceName,
}) => {
  const intelEnabled = useFeatureFlagsStore((s) => s.intel);
  const [promoteTarget, setPromoteTarget] = useState<PromoteTarget | null>(null);
  const [tab, setTab] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // Baseline state
  const [lookbackHours, setLookbackHours] = useState(24);
  const [signalTypes, setSignalTypes] = useState<string[]>([...SIGNAL_TYPES]);
  const [minSightings, setMinSightings] = useState(2);
  const [excludeCotravelers, setExcludeCotravelers] = useState(true);
  const [devices, setDevices] = useState<BaselineDevice[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [scanning, setScanning] = useState(false);
  const [diffMode, setDiffMode] = useState(false);
  const [sessions, setSessions] = useState<BaselineSession[]>([]);
  const activeSession = sessions.find(s => s.status === 'capturing') || null;

  // Trusted / watchlist state
  const [trusted, setTrusted] = useState<TrustedDevice[]>([]);
  const [watchlist, setWatchlist] = useState<WatchlistEntry[]>([]);

  // New watchlist entry form
  const [wlIdentifier, setWlIdentifier] = useState('');
  const [wlType, setWlType] = useState('mac');
  const [wlMode, setWlMode] = useState<'exact' | 'pattern'>('exact');
  const [wlPattern, setWlPattern] = useState('');
  const [wlSignal, setWlSignal] = useState('any');
  const [wlSeverity, setWlSeverity] = useState('critical');
  const [wlScoped, setWlScoped] = useState(true);
  const [wlLabel, setWlLabel] = useState('');

  const resetSelection = () => setSelected(new Set());

  const runScan = useCallback(async (
    diff: boolean,
    windowOverride?: { start: string; end: string; minSightings?: number },
  ) => {
    if (geofenceId == null) return;
    setScanning(true);
    setError(null);
    setDiffMode(diff);
    resetSelection();
    try {
      const resp = diff
        ? await forceProtectionService.baselineDiff(geofenceId, signalTypes)
        : await forceProtectionService.baselineScan({
            geofence_id: geofenceId,
            start: windowOverride?.start
              ?? new Date(Date.now() - lookbackHours * 3600_000).toISOString(),
            end: windowOverride?.end ?? new Date().toISOString(),
            signal_types: signalTypes,
            min_sightings: windowOverride?.minSightings ?? minSightings,
            exclude_cotravelers: excludeCotravelers,
          });
      setDevices(resp.devices);
      setInfo(resp.devices.length === 0
        ? 'No devices found in this window.'
        : `Found ${resp.devices.length} device(s).`);
    } catch (e: any) {
      setError(e.message || 'Scan failed');
    } finally {
      setScanning(false);
    }
  }, [geofenceId, signalTypes, lookbackHours, minSightings, excludeCotravelers]);

  const loadSessions = useCallback(async () => {
    if (geofenceId == null) return;
    try { setSessions(await forceProtectionService.listSessions(geofenceId)); }
    catch { /* sessions are optional */ }
  }, [geofenceId]);

  const loadTrusted = useCallback(async () => {
    try { setTrusted(await forceProtectionService.listTrusted()); }
    catch (e: any) { setError(e.message); }
  }, []);

  const loadWatchlist = useCallback(async () => {
    try { setWatchlist(await forceProtectionService.listWatchlist()); }
    catch (e: any) { setError(e.message); }
  }, []);

  useEffect(() => {
    if (!open || geofenceId == null) return;
    setDevices([]);
    resetSelection();
    setError(null);
    setInfo(null);
    loadSessions();
    // Load trusted + watchlist up front so the tab badge counts are accurate
    // without having to visit each tab first.
    loadTrusted();
    loadWatchlist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, geofenceId, tab]);

  const toggle = (key: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    const selectable = devices.filter(d => !d.already_trusted);
    if (selected.size >= selectable.length && selectable.length > 0) {
      resetSelection();
    } else {
      setSelected(new Set(selectable.map(d => d.normalized_identifier)));
    }
  };

  const selectedDevices = devices.filter(d => selected.has(d.normalized_identifier));

  const trustSelected = async () => {
    if (geofenceId == null || selectedDevices.length === 0) return;
    setError(null);
    try {
      const items = selectedDevices.map(d => ({
        identifier: d.identifier || d.normalized_identifier,
        identifier_type: d.signal_type === 'cellular' ? 'device_id' : 'mac',
        label: d.display_name || d.manufacturer || undefined,
        applies_to: d.signal_type === 'wifi' ? 'wifi' : d.signal_type === 'bluetooth' ? 'bluetooth' : 'all',
      }));
      const res = await forceProtectionService.bulkApprove({
        devices: items,
        geofence_id: geofenceId,
        reason: `Baseline-approved for ${geofenceName || `fence ${geofenceId}`}`,
      });
      setInfo(`Trusted ${res.approved} device(s).`);
      resetSelection();
      runScan(diffMode);
    } catch (e: any) {
      setError(e.message || 'Bulk approve failed');
    }
  };

  const watchlistSelected = async () => {
    if (selectedDevices.length === 0) return;
    setError(null);
    try {
      for (const d of selectedDevices) {
        await forceProtectionService.createWatchlist({
          identifier: d.identifier || d.normalized_identifier,
          identifier_type: d.signal_type === 'wifi' || d.signal_type === 'bluetooth' ? 'mac' : 'device_id',
          match_mode: 'exact',
          signal_type: d.signal_type,
          geofence_id: geofenceId ?? undefined,
          severity_floor: 'critical',
          label: d.display_name || d.manufacturer || 'Flagged from baseline',
        });
      }
      setInfo(`Added ${selectedDevices.length} device(s) to watchlist.`);
      resetSelection();
      runScan(diffMode);
    } catch (e: any) {
      setError(e.message || 'Add to watchlist failed');
    }
  };

  const handleStartCapture = async () => {
    if (geofenceId == null) return;
    setError(null);
    try {
      const started = await forceProtectionService.startSession(geofenceId, geofenceName);
      // Update local state directly so the button flips immediately, even if
      // the follow-up list refresh fails or lags.
      setSessions(prev => [started, ...prev.filter(p => p.id !== started.id)]);
      setInfo('Capture session started — devices seen from now until you stop will form the baseline.');
      loadSessions();
    } catch (e: any) {
      setError(e.message || 'Could not start capture');
    }
  };
  const handleStopCapture = async () => {
    if (!activeSession) return;
    const sess = activeSession;
    setError(null);
    try {
      const stopped = await forceProtectionService.stopSession(sess.id);
      // Reflect the stop locally so the button flips regardless of the list GET.
      setSessions(prev => prev.map(p => (p.id === stopped.id ? stopped : p)));
      setInfo('Capture stopped — scanning the captured window…');
      loadSessions();
      // Surface exactly what was seen during the capture window so the baseline
      // appears immediately (min_sightings=1 since capture windows are short).
      await runScan(false, {
        start: stopped.started_at,
        end: stopped.ended_at || new Date().toISOString(),
        minSightings: 1,
      });
    } catch (e: any) {
      setError(e.message || 'Could not stop capture');
    }
  };

  const addWatchlistEntry = async () => {
    setError(null);
    try {
      await forceProtectionService.createWatchlist({
        identifier: wlMode === 'exact' ? wlIdentifier : undefined,
        identifier_type: wlType,
        match_mode: wlMode,
        pattern: wlMode === 'pattern' ? wlPattern : undefined,
        signal_type: wlSignal,
        severity_floor: wlSeverity,
        geofence_id: wlScoped && geofenceId != null ? geofenceId : undefined,
        label: wlLabel || undefined,
      });
      setWlIdentifier(''); setWlPattern(''); setWlLabel('');
      await loadWatchlist();
      setInfo('Watchlist entry added.');
    } catch (e: any) {
      setError(e.message || 'Could not add watchlist entry');
    }
  };

  const fenceTrusted = trusted.filter(t => t.geofence_id === geofenceId || t.geofence_id == null);
  const fenceWatchlist = watchlist.filter(w => w.geofence_id === geofenceId || w.geofence_id == null);

  const fmtAge = (ts?: string | null) => {
    if (!ts) return '—';
    const mins = Math.floor((Date.now() - new Date(ts).getTime()) / 60000);
    if (mins < 1) return '<1m';
    if (mins < 60) return `${mins}m`;
    if (mins < 1440) return `${Math.floor(mins / 60)}h`;
    return `${Math.floor(mins / 1440)}d`;
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ShieldIcon color="primary" />
        Force Protection — {geofenceName || (geofenceId != null ? `Fence ${geofenceId}` : 'No fence selected')}
      </DialogTitle>
      <DialogContent dividers>
        {geofenceId == null ? (
          <Alert severity="info">Select a geofence to manage Force Protection.</Alert>
        ) : (
          <>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
              <Tab label="Baseline Review" />
              <Tab label={`Trusted (${fenceTrusted.length})`} />
              <Tab label={`Watchlist (${fenceWatchlist.length})`} />
            </Tabs>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>{info}</Alert>}

            {/* ---- Baseline Review ---- */}
            {tab === 0 && (
              <Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 2 }}>
                  <TextField
                    label="Lookback (hours)" type="number" size="small"
                    value={lookbackHours}
                    onChange={e => setLookbackHours(Math.max(1, parseInt(e.target.value) || 1))}
                    sx={{ width: 140 }}
                  />
                  <TextField
                    label="Min sightings" type="number" size="small"
                    value={minSightings}
                    onChange={e => setMinSightings(Math.max(1, parseInt(e.target.value) || 1))}
                    sx={{ width: 130 }}
                  />
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Signal types</InputLabel>
                    <Select
                      multiple value={signalTypes} label="Signal types"
                      renderValue={(v) => (v as string[]).join(', ')}
                      onChange={e => setSignalTypes(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value as string[])}
                    >
                      {SIGNAL_TYPES.map(s => (
                        <MenuItem key={s} value={s}>
                          <Checkbox checked={signalTypes.indexOf(s) > -1} size="small" />{s}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    control={<Checkbox checked={excludeCotravelers} onChange={e => setExcludeCotravelers(e.target.checked)} size="small" />}
                    label="Exclude co-travelers"
                  />
                  <Button variant="contained" startIcon={scanning ? <CircularProgress size={16} /> : <RefreshIcon />}
                    onClick={() => runScan(false)} disabled={scanning}>
                    Run Baseline Scan
                  </Button>
                  <Button variant="outlined" onClick={() => runScan(true)} disabled={scanning}>
                    New Since Last Review
                  </Button>
                </Box>

                {/* Capture session controls + periodic-review banner */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  {activeSession ? (
                    <Button color="error" size="small" startIcon={<StopIcon />} onClick={handleStopCapture}>
                      Stop Capture (started {fmtAge(activeSession.started_at)} ago)
                    </Button>
                  ) : (
                    <Button size="small" startIcon={<PlayIcon />} onClick={handleStartCapture}>
                      Start Capture Session
                    </Button>
                  )}
                  {diffMode && (
                    <Chip color="warning" size="small" icon={<WarningIcon />}
                      label={`${devices.length} new device(s) since last review`} />
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {activeSession
                      ? 'Capturing… stop to review devices seen during this window.'
                      : 'Run a scan over the lookback window, or start a capture session to baseline a live window.'}
                  </Typography>
                </Box>

                {devices.length > 0 && (
                  <>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <Button size="small" variant="contained" color="success" startIcon={<TrustIcon />}
                        disabled={selectedDevices.length === 0} onClick={trustSelected}>
                        Trust Selected ({selectedDevices.length})
                      </Button>
                      <Button size="small" variant="outlined" color="warning" startIcon={<WarningIcon />}
                        disabled={selectedDevices.length === 0} onClick={watchlistSelected}>
                        Add to Watchlist
                      </Button>
                    </Box>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox size="small" onChange={toggleAll}
                              checked={selectedDevices.length > 0 && selectedDevices.length === devices.filter(d => !d.already_trusted).length} />
                          </TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Identifier</TableCell>
                          <TableCell>Name / Vendor</TableCell>
                          <TableCell align="right">Sightings</TableCell>
                          <TableCell align="right">Max RSSI</TableCell>
                          <TableCell align="right">Last seen</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {devices.map(d => (
                          <TableRow key={`${d.signal_type}:${d.normalized_identifier}`} hover>
                            <TableCell padding="checkbox">
                              <Checkbox size="small" disabled={d.already_trusted}
                                checked={selected.has(d.normalized_identifier)}
                                onChange={() => toggle(d.normalized_identifier)} />
                            </TableCell>
                            <TableCell>{d.signal_type}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {d.identifier || d.normalized_identifier}
                            </TableCell>
                            <TableCell>{d.display_name || d.manufacturer || '—'}</TableCell>
                            <TableCell align="right">{d.sighting_count}</TableCell>
                            <TableCell align="right">{d.max_rssi ?? '—'}</TableCell>
                            <TableCell align="right">{fmtAge(d.last_seen)}</TableCell>
                            <TableCell>
                              {d.already_trusted && <Chip size="small" color="success" label="trusted" />}
                              {d.on_watchlist && <Chip size="small" color="error" label="watchlist" sx={{ ml: 0.5 }} />}
                              {d.is_tracker && <Chip size="small" color="warning" label="tracker" sx={{ ml: 0.5 }} />}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                )}
              </Box>
            )}

            {/* ---- Trusted ---- */}
            {tab === 1 && (
              <Box>
                <Button size="small" startIcon={<RefreshIcon />} onClick={loadTrusted} sx={{ mb: 1 }}>Refresh</Button>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Identifier</TableCell>
                      <TableCell>Label</TableCell>
                      <TableCell>Scope</TableCell>
                      <TableCell>Fence</TableCell>
                      <TableCell>Reviewed</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fenceTrusted.map(t => (
                      <TableRow key={t.id} hover>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{t.identifier}</TableCell>
                        <TableCell>{t.label || '—'}</TableCell>
                        <TableCell>{t.applies_to}</TableCell>
                        <TableCell>{t.geofence_id == null ? 'global' : t.geofence_id === geofenceId ? 'this fence' : t.geofence_id}</TableCell>
                        <TableCell>{fmtAge(t.last_reviewed_at)}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={async () => { await forceProtectionService.deleteTrusted(t.id); loadTrusted(); }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {fenceTrusted.length === 0 && (
                      <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary">No trusted devices.</Typography></TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            )}

            {/* ---- Watchlist ---- */}
            {tab === 2 && (
              <Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
                  <FormControl size="small" sx={{ width: 130 }}>
                    <InputLabel>Match</InputLabel>
                    <Select value={wlMode} label="Match" onChange={e => setWlMode(e.target.value as any)}>
                      <MenuItem value="exact">Exact</MenuItem>
                      <MenuItem value="pattern">Pattern</MenuItem>
                    </Select>
                  </FormControl>
                  {wlMode === 'exact' ? (
                    <TextField size="small" label="Identifier (MAC/BSSID/SSID)" value={wlIdentifier}
                      onChange={e => setWlIdentifier(e.target.value)} sx={{ width: 240 }} />
                  ) : (
                    <TextField size="small" label="Pattern (OUI hex or regex)" value={wlPattern}
                      onChange={e => setWlPattern(e.target.value)} sx={{ width: 240 }} />
                  )}
                  <FormControl size="small" sx={{ width: 120 }}>
                    <InputLabel>Type</InputLabel>
                    <Select value={wlType} label="Type" onChange={e => setWlType(e.target.value)}>
                      {['mac', 'bssid', 'ssid', 'name', 'oui', 'device_id', 'other'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ width: 120 }}>
                    <InputLabel>Signal</InputLabel>
                    <Select value={wlSignal} label="Signal" onChange={e => setWlSignal(e.target.value)}>
                      {['any', 'wifi', 'bluetooth', 'cellular'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ width: 120 }}>
                    <InputLabel>Severity</InputLabel>
                    <Select value={wlSeverity} label="Severity" onChange={e => setWlSeverity(e.target.value)}>
                      {['info', 'warning', 'critical'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <TextField size="small" label="Label" value={wlLabel} onChange={e => setWlLabel(e.target.value)} sx={{ width: 160 }} />
                  <FormControl size="small" sx={{ width: 130 }}>
                    <InputLabel>Scope</InputLabel>
                    <Select value={wlScoped ? 'fence' : 'global'} label="Scope"
                      onChange={e => setWlScoped(e.target.value === 'fence')}>
                      <MenuItem value="fence">This fence</MenuItem>
                      <MenuItem value="global">Global</MenuItem>
                    </Select>
                  </FormControl>
                  <Button variant="contained" onClick={addWatchlistEntry}>Add</Button>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Match</TableCell>
                      <TableCell>Value</TableCell>
                      <TableCell>Signal</TableCell>
                      <TableCell>Severity</TableCell>
                      <TableCell>Fence</TableCell>
                      <TableCell>Label</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fenceWatchlist.map(w => (
                      <TableRow key={w.id} hover>
                        <TableCell>{w.match_mode}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{w.normalized_identifier || w.pattern}</TableCell>
                        <TableCell>{w.signal_type}</TableCell>
                        <TableCell><Chip size="small" color={w.severity_floor === 'critical' ? 'error' : w.severity_floor === 'warning' ? 'warning' : 'default'} label={w.severity_floor} /></TableCell>
                        <TableCell>{w.geofence_id == null ? 'global' : w.geofence_id === geofenceId ? 'this fence' : w.geofence_id}</TableCell>
                        <TableCell>{w.label || '—'}</TableCell>
                        <TableCell align="right">
                          {intelEnabled && (
                            <Tooltip title="Add to Intel Packet">
                              <IconButton
                                size="small"
                                onClick={() => setPromoteTarget({
                                  member_type: 'watchlist',
                                  member_id: String(w.id),
                                  label: w.label || w.normalized_identifier || w.pattern || `Watchlist ${w.id}`,
                                  is_primary: true,
                                  meta: {
                                    identifier: w.normalized_identifier || w.identifier,
                                    identifier_type: w.identifier_type,
                                    geofence_id: w.geofence_id ?? geofenceId,
                                  },
                                })}
                              >
                                <FactCheckIcon fontSize="small" sx={{ color: '#ffb300' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <IconButton size="small" onClick={async () => { await forceProtectionService.deleteWatchlist(w.id); loadWatchlist(); }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {fenceWatchlist.length === 0 && (
                      <TableRow><TableCell colSpan={7}><Typography variant="body2" color="text.secondary">No watchlist entries.</Typography></TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
      <PromoteToPacketDialog
        open={!!promoteTarget}
        onClose={() => setPromoteTarget(null)}
        target={promoteTarget}
        extraMembers={geofenceId != null ? [{
          member_type: 'geofence',
          member_id: String(geofenceId),
          label: geofenceName,
        }] : []}
        defaultName={promoteTarget?.label || geofenceName}
      />
    </>
  );
};

export default ForceProtectionDialog;
